"use client";

import {
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type FormEvent,
  type RefObject,
} from "react";
import dynamic from "next/dynamic";
import { calculateMatching } from "@/lib/api/kundali";
import type { PersonInput, MatchingResponse } from "@/lib/api/kundali";
import type { PlaceResult } from "@/lib/api/misc";
import Input from "@ishvaram/ui/Input";
import Button from "@ishvaram/ui/Button";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { trackCalculatorUsed, trackMatchingCompleted } from "@/lib/analytics-free-tools";
import type { MatchingResultsPanelProps } from "@/components/matching/MatchingResultsPanel";
import { inspectMatchingResult } from "@/lib/matching-result-quality";
import { jsErrorFieldsFromUnknown, reportJsError } from "@/lib/js-error-beacon";
import { MatchingModeToggle, type MatchingMode } from "@/components/matching/MatchingModeToggle";
import { NaamMatchingMode } from "@/components/matching/naam/NaamMatchingMode";

const loadMatchingResultsPanel = () =>
  import("@/components/matching/MatchingResultsPanel").then((mod) => mod.MatchingResultsPanel);

const AIAstrologerCTA = dynamic(() => import("@/components/AIAstrologerCTA"), {
  ssr: false,
  loading: () => null,
});

interface FormState {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  place: PlaceResult | null;
}

const empty = (): FormState => ({
  name: "",
  birthDate: "",
  birthTime: "",
  birthPlace: "",
  place: null,
});

function PersonPanel({
  label,
  icon,
  data,
  onChange,
}: {
  label: string;
  icon: string;
  data: FormState;
  onChange: (patch: Partial<FormState>) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const id = label.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="card-sacred p-5 sm:p-6 space-y-4 flex-1">
      <h3
        className="text-[16px] font-bold flex items-center gap-2"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        <span>{icon}</span> {label}
      </h3>

      <Input
        id={`name-${id}`}
        type="text"
        required
        label="Full Name"
        value={data.name}
        onChangeText={(v) => onChange({ name: v })}
        placeholder="Enter full name"
        autoComplete="name"
        autoCapitalize="words"
      />

      <Input
        id={`date-${id}`}
        type="date"
        required
        label="Birth Date"
        value={data.birthDate}
        onChangeText={(v) => onChange({ birthDate: v })}
        min="1900-01-01"
        max={today}
      />

      <Input
        id={`time-${id}`}
        type="time"
        label="Birth Time (needed for an exact score)"
        value={data.birthTime}
        onChangeText={(v) => onChange({ birthTime: v })}
      />

      <div>
        <label
          htmlFor={`place-${id}`}
          className="block text-body-small font-semibold mb-2"
          style={{ color: "var(--ish-dark)" }}
        >
          Birth Place
        </label>
        <PlaceAutocomplete
          id={`place-${id}`}
          value={data.birthPlace}
          onChange={(v) => onChange({ birthPlace: v, place: null })}
          onSelect={(p) => onChange({ birthPlace: p.display, place: p })}
        />
      </div>
    </div>
  );
}

function toPersonInput(f: FormState): PersonInput {
  return {
    name: f.name,
    birth_date: f.birthDate,
    birth_time: f.birthTime || null,
    birth_place: f.birthPlace,
    ...(f.place && {
      latitude: f.place.latitude,
      longitude: f.place.longitude,
      timezone: f.place.timezone,
    }),
  } as PersonInput;
}

function assertCompleteGunMilanResult(result: MatchingResponse, language: "en" | "hi"): void {
  const quality = inspectMatchingResult(result);
  if (quality.complete) return;
  reportJsError({
    error_name: "MatchingResultContractViolation",
    error_message: `gun milan result incomplete: ${quality.reasons.join(",")}`,
    boundary: `GunMilanClient.result.${quality.reasons[0] ?? "unknown"}`,
  });
  throw new Error(
    language === "hi"
      ? "पूरा गुण मिलान परिणाम तैयार नहीं हो सका। कृपया फिर प्रयास करें।"
      : "Your complete Gun Milan result could not be prepared. Please try again."
  );
}

export default function GunMilanClient({
  language = "en",
}: {
  language?: "en" | "hi";
} = {}) {
  const [boy, setBoy] = useState<FormState>(empty());
  const [girl, setGirl] = useState<FormState>(empty());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MatchingResponse | null>(null);
  const [ResultsPanel, setResultsPanel] = useState<ComponentType<MatchingResultsPanelProps> | null>(
    null
  );
  const resultsRef = useRef<HTMLDivElement>(null);
  const hasResult = Boolean(result);
  // Local state only — no query param, no hash, no router push. A crawler must
  // never see a second address for this page (rule 66 expansion freeze).
  const [mode, setMode] = useState<MatchingMode>("birth");

  useLayoutEffect(() => {
    if (!hasResult) return;
    resultsRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
  }, [hasResult]);

  async function handleCalculate(e: FormEvent) {
    e.preventDefault();
    // Start the result-only chunk with the API request, then commit the result
    // only after both are ready. Rendering the short outer fallback before the
    // ~2.8kpx result made scroll anchoring leave a visible residual CLS shift.
    const resultsPanelReady = loadMatchingResultsPanel();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const [res, ResultsPanelComponent] = await Promise.all([
        calculateMatching({
          person_a: toPersonInput(boy),
          person_b: toPersonInput(girl),
        }),
        resultsPanelReady,
      ]);
      assertCompleteGunMilanResult(res, language);
      setResultsPanel(() => ResultsPanelComponent);
      setResult(res);
      trackCalculatorUsed({ calculator: "gun-milan" });
      trackMatchingCompleted({ score: res.total_score ?? 0 });
    } catch (err) {
      reportJsError({
        ...jsErrorFieldsFromUnknown(err),
        boundary: "GunMilanClient.calculation_failed",
      });
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setBoy(empty());
    setGirl(empty());
    setResult(null);
    setError(null);
  }

  if (mode === "naam") {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
        <MatchingModeToggle mode={mode} language={language} onChange={setMode} />
        <NaamMatchingMode
          language={language}
          surface="gun-milan"
          onSwitchToBirthMode={() => setMode("birth")}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      {!result || !ResultsPanel ? (
        <>
          <MatchingModeToggle mode={mode} language={language} onChange={setMode} />
          <GunMilanForm
            boy={boy}
            girl={girl}
            loading={loading}
            error={error}
            onSubmit={handleCalculate}
            onBoyChange={(patch) => setBoy((prev) => ({ ...prev, ...patch }))}
            onGirlChange={(patch) => setGirl((prev) => ({ ...prev, ...patch }))}
          />
        </>
      ) : (
        <GunMilanResults
          result={result}
          boy={boy}
          girl={girl}
          resultsRef={resultsRef}
          onReset={handleReset}
          language={language}
          ResultsPanel={ResultsPanel}
        />
      )}
    </div>
  );
}

function GunMilanForm({
  boy,
  girl,
  loading,
  error,
  onSubmit,
  onBoyChange,
  onGirlChange,
}: {
  boy: FormState;
  girl: FormState;
  loading: boolean;
  error: string | null;
  onSubmit: (event: FormEvent) => void;
  onBoyChange: (patch: Partial<FormState>) => void;
  onGirlChange: (patch: Partial<FormState>) => void;
}) {
  return (
    <form onSubmit={onSubmit} noValidate>
      <h2 className="sr-only">Enter both birth profiles for Gun Milan</h2>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <PersonPanel label="Boy (वर)" icon="♂" data={boy} onChange={onBoyChange} />
        <PersonPanel label="Girl (वधू)" icon="♀" data={girl} onChange={onGirlChange} />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 text-body-small rounded-lg p-3.5 text-center"
          style={{
            color: "var(--color-error-text)",
            backgroundColor: "var(--color-error-bg)",
            border: "1px solid var(--color-error-tint)",
          }}
        >
          {error}
        </div>
      )}

      <Button
        type="submit"
        onPress={() => {}}
        disabled={loading}
        loading={loading}
        variant="primary"
        title={loading ? "Calculating Gun Milan..." : "Calculate Gun Milan →"}
        className="w-full py-4 text-body font-semibold"
      />

      <p className="mt-3 text-center text-caption text-ish-text-faint">
        36-point Ashtakoot score &middot; All 8 Kootas &middot; Mangal Dosha check &middot; 100%
        free
      </p>
    </form>
  );
}

function GunMilanResults({
  result,
  boy,
  girl,
  resultsRef,
  onReset,
  language,
  ResultsPanel,
}: {
  result: MatchingResponse;
  boy: FormState;
  girl: FormState;
  resultsRef: RefObject<HTMLDivElement | null>;
  onReset: () => void;
  language: "en" | "hi";
  ResultsPanel: ComponentType<MatchingResultsPanelProps>;
}) {
  return (
    <div ref={resultsRef} className="space-y-6">
      <NamesBanner boyName={boy.name} girlName={girl.name} />
      <ResultsPanel
        surface="gun-milan"
        result={result}
        personAData={toPersonInput(boy)}
        personBData={toPersonInput(girl)}
        defaultLanguage={language}
        onReset={onReset}
        showDetailedReport={false}
      />
      <AIAstrologerCTA context="matching" />
    </div>
  );
}

function NamesBanner({ boyName, girlName }: { boyName: string; girlName: string }) {
  return (
    <div
      className="card-sacred p-4 flex items-center justify-center gap-3 text-center"
      style={{ borderColor: "var(--color-accent-border)" }}
    >
      <span className="text-[16px] font-bold text-ish-dark">{boyName}</span>
      <span style={{ color: "var(--intent-brand-text)" }}>♥</span>
      <span className="text-[16px] font-bold text-ish-dark">{girlName}</span>
    </div>
  );
}
